/**
 * Global Alert Inbox — Section 04, Security Center UI/UX Blueprint v2.0
 *
 * Top-level landing page aggregating active alerts from all 5 Mission Centers.
 * Priority drives sort order. This is the first screen an admin lands on after login.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Filter,
  X,
  ShieldAlert,
  Building2,
  Server,
  Lock,
  Inbox,
  ArrowRight,
  ScanLine,
  Zap,
  Shield,
  TrendingDown,
  FileCheck,
  GitBranch,
  ClipboardList,
} from 'lucide-react';
import api from '../api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

// ── Types ────────────────────────────────────────────────────────────────────

type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';
type AlertStatus = 'active' | 'acknowledged' | 'resolved' | 'dismissed';
type AlertSource = 'compliance_drift' | 'vendor' | 'asset' | 'privileged_access' | 'security_scan' | 'ai_ops' | 'tenant_isolation' | 'compliance_score' | 'evidence' | 'control_mapping' | 'audit';

interface SuggestedAction {
  label: string;
  route: string;
}

interface UnifiedAlert {
  uid: string;
  source: AlertSource;
  sourceLabel: string;
  severity: Severity;
  status: AlertStatus;
  title: string;
  message: string;
  relatedEntity: { type: string; id: number | null; name: string };
  framework: string | null;
  dueDate: string | null;
  daysUntilDue: number | null;
  suggestedAction: SuggestedAction;
  createdAt: string;
  updatedAt: string;
  notes?: string | null;
}

interface AlertSummary {
  total: number;
  bySource: Record<string, number>;
  bySeverity: Record<Severity, number>;
}

// ── Visual config ────────────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<Severity, { icon: React.ElementType; color: string; bg: string; border: string; badge: string }> = {
  critical: { icon: AlertTriangle, color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700' },
  high:     { icon: AlertCircle,   color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700' },
  medium:   { icon: AlertCircle,   color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700' },
  low:      { icon: Info,          color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700' },
  info:     { icon: Info,          color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', badge: 'bg-gray-100 text-gray-600' },
};

const SOURCE_CONFIG: Record<AlertSource, { icon: React.ElementType; color: string; label: string }> = {
  compliance_drift:  { icon: ShieldAlert, color: 'text-purple-600', label: 'Compliance Drift' },
  vendor:            { icon: Building2,   color: 'text-blue-600',   label: 'Vendor Mgmt' },
  asset:             { icon: Server,      color: 'text-teal-600',   label: 'Asset Inventory' },
  privileged_access: { icon: Lock,        color: 'text-slate-600',  label: 'Privileged Access' },
  security_scan:     { icon: ScanLine,      color: 'text-rose-600',   label: 'Security Scan' },
  ai_ops:            { icon: Zap,           color: 'text-yellow-600', label: 'AI Operations' },
  tenant_isolation:  { icon: Shield,        color: 'text-indigo-600', label: 'Tenant Isolation' },
  compliance_score:  { icon: TrendingDown,  color: 'text-red-600',    label: 'Compliance Score' },
  evidence:          { icon: FileCheck,     color: 'text-green-700',  label: 'Evidence Collection' },
  control_mapping:   { icon: GitBranch,     color: 'text-violet-600', label: 'Control Mapping' },
  audit:             { icon: ClipboardList, color: 'text-orange-600', label: 'Audit Log' },
};

const STATUS_TABS: { value: string; label: string }[] = [
  { value: 'active',       label: 'Active' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved',     label: 'Resolved' },
  { value: 'dismissed',    label: 'Dismissed' },
];

// ── Component ────────────────────────────────────────────────────────────────

const GlobalAlertInbox: React.FC = () => {
  const navigate = useNavigate();

  const [alerts, setAlerts]       = useState<UnifiedAlert[]>([]);
  const [summary, setSummary]     = useState<AlertSummary | null>(null);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedUids, setSelectedUids] = useState<Set<string>>(new Set());
  const [bulkDismissing, setBulkDismissing] = useState(false);

  // Filters
  const [statusTab, setStatusTab]     = useState<string>('active');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter]     = useState<string>('');
  const [expandedUid, setExpandedUid]       = useState<string | null>(null);
  const [notesMap, setNotesMap]             = useState<Record<string, string>>({});
  const [editingNoteUid, setEditingNoteUid] = useState<string | null>(null);

  // ── Fetch summary ──────────────────────────────────────────────────────────
  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/global-alerts/summary');
      if (res.data.success) setSummary(res.data.summary);
    } catch { /* non-critical */ }
  }, []);

  // ── Fetch alerts ───────────────────────────────────────────────────────────
  const fetchAlerts = useCallback(async (showRefreshSpinner = false) => {
    if (showRefreshSpinner) setRefreshing(true);
    else setLoading(true);
    try {
      const params: Record<string, string> = { status: statusTab, limit: '100' };
      if (severityFilter) params.severity = severityFilter;
      if (sourceFilter)   params.source   = sourceFilter;
      const res = await api.get('/api/admin/global-alerts', { params });
      if (res.data.success) {
        const fetched: UnifiedAlert[] = res.data.alerts || [];
        setAlerts(fetched);
        // Pre-populate textarea with any notes already saved on acknowledged alerts
        const existingNotes: Record<string, string> = {};
        fetched.forEach(a => { if (a.notes) existingNotes[a.uid] = a.notes; });
        // DB notes always win — overwrite stale local entries so saved notes display correctly
        setNotesMap(prev => ({ ...prev, ...existingNotes }));
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to load alerts');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusTab, severityFilter, sourceFilter]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleSaveNote = async (uid: string) => {
    const notes = notesMap[uid];
    if (!notes?.trim()) return;
    setActionLoading(uid + 'savenote');
    try {
      await api.put(`/api/admin/global-alerts/${uid}/note`, { notes });
      toast.success('Note saved');
      setNotesMap(prev => { const n = { ...prev }; delete n[uid]; return n; });
      fetchAlerts();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to save note');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleSelect = (uid: string) => {
    setSelectedUids(prev => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  };

  const handleBulkDismiss = async () => {
    if (selectedUids.size === 0) return;
    setBulkDismissing(true);
    try {
      await Promise.all(Array.from(selectedUids).map(uid => api.put(`/api/admin/global-alerts/${uid}/dismiss`, {})));
      toast.success(`${selectedUids.size} alert(s) dismissed`);
      setSelectedUids(new Set());
      fetchAlerts();
      fetchSummary();
    } catch {
      toast.error('Some alerts could not be dismissed');
    } finally {
      setBulkDismissing(false);
    }
  };

  const handleAction = async (uid: string, action: 'acknowledge' | 'resolve' | 'dismiss') => {
    setActionLoading(uid + action);
    try {
      const notes = notesMap[uid] || undefined;
      await api.put(`/api/admin/global-alerts/${uid}/${action}`, { notes });
      toast.success(action === 'acknowledge' ? 'Acknowledged' : action === 'resolve' ? 'Resolved' : 'Dismissed');
      setNotesMap(prev => { const n = { ...prev }; delete n[uid]; return n; });
      fetchAlerts();
      fetchSummary();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || `Failed to ${action}`);
    } finally {
      setActionLoading(null);
    }
  };

  const navigateToSource = (alert: UnifiedAlert) => {
    const route = alert.suggestedAction.route;
    if (!route) return;
    // Admin.tsx reads ?tab= on mount and calls navigateToSecurityCenter(tab),
    // which handles all Mission Center routing. For standalone pages, navigate directly.
    const ADMIN_TABS = new Set([
      'compliance-drift', 'vendor-management', 'asset-inventory', 'security-documents',
      'audit-dashboard', 'privileged-access', 'tenant-isolation', 'security-scan',
      'vulnerability-management', 'usage-dashboard', 'ai-activity',
    ]);
    if (ADMIN_TABS.has(route)) {
      navigate(`/app/admin?tab=${route}`);
    } else {
      navigate(`/app/${route}`);
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────────
  const renderSeverityBadge = (severity: Severity) => {
    const cfg = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${cfg.badge}`}>
        <Icon className="w-3 h-3" />
        {severity}
      </span>
    );
  };

  const renderSourceBadge = (source: AlertSource) => {
    const cfg = SOURCE_CONFIG[source] || { icon: Info, color: 'text-gray-500', label: source };
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${cfg.color}`}>
        <Icon className="w-3 h-3" />
        {cfg.label}
      </span>
    );
  };

  const formatDate = (d: string | null) => {
    if (!d) return null;
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // ── Summary stats bar ──────────────────────────────────────────────────────
  const renderSummaryBar = () => {
    if (!summary) return null;
    const sev = summary.bySeverity;
    const stats = [
      { label: 'Critical', value: sev.critical || 0, color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
      { label: 'High',     value: sev.high || 0,     color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200' },
      { label: 'Medium',   value: sev.medium || 0,   color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
      { label: 'Low',      value: sev.low || 0,      color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
    ];
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {stats.map(s => (
          <div key={s.label} className={`rounded-xl border p-4 ${s.bg}`}>
            <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
            <p className={`text-xs font-semibold mt-0.5 ${s.color} opacity-80`}>{s.label}</p>
          </div>
        ))}
      </div>
    );
  };

  // ── Alert row ──────────────────────────────────────────────────────────────
  const renderAlertRow = (alert: UnifiedAlert) => {
    const sevCfg = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
    const isExpanded = expandedUid === alert.uid;
    const isActing = (a: string) => actionLoading === alert.uid + a;

    const isSelected = selectedUids.has(alert.uid);

    return (
      <div key={alert.uid} className={`rounded-xl border ${isSelected ? 'border-blue-400 bg-blue-50/30' : sevCfg.border + ' bg-white'} overflow-hidden transition-shadow hover:shadow-sm`}>
        {/* Main row */}
        <div
          className="flex items-start gap-3 p-4 cursor-pointer select-none"
          onClick={() => setExpandedUid(isExpanded ? null : alert.uid)}
        >
          {/* Checkbox */}
          <input
            type="checkbox"
            checked={isSelected}
            onChange={e => { e.stopPropagation(); toggleSelect(alert.uid); }}
            onClick={e => e.stopPropagation()}
            className="mt-1 shrink-0 w-4 h-4 accent-blue-600 cursor-pointer"
          />
          {/* Severity indicator strip */}
          <div className={`w-1 self-stretch rounded-full shrink-0 ${
            alert.severity === 'critical' ? 'bg-red-500' :
            alert.severity === 'high' ? 'bg-orange-500' :
            alert.severity === 'medium' ? 'bg-amber-500' :
            alert.severity === 'low' ? 'bg-blue-400' : 'bg-gray-300'
          }`} />

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              {renderSeverityBadge(alert.severity)}
              {renderSourceBadge(alert.source)}
              {alert.relatedEntity?.name && (
                <span className="text-[11px] text-gray-500 font-medium">· {alert.relatedEntity.name}</span>
              )}
              {alert.daysUntilDue != null && alert.daysUntilDue <= 30 && (
                <span className="text-[10px] bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5 rounded font-bold">
                  {alert.daysUntilDue <= 0 ? 'OVERDUE' : `${alert.daysUntilDue}d`}
                </span>
              )}
            </div>
            <p className="text-sm font-semibold text-gray-900 leading-tight">{alert.title}</p>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{alert.message}</p>
          </div>

          {/* Suggested action button */}
          <div className="shrink-0 flex items-center gap-2">
            <button
              onClick={e => { e.stopPropagation(); navigateToSource(alert); }}
              className="hidden sm:flex items-center gap-1 px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-700 transition-colors whitespace-nowrap"
            >
              {alert.suggestedAction.label}
              <ArrowRight className="w-3 h-3" />
            </button>
            {isExpanded
              ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
              : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
            }
          </div>
        </div>

        {/* Expanded detail */}
        {isExpanded && (
          <div className={`border-t ${sevCfg.border} ${sevCfg.bg} px-4 pb-4 pt-3 space-y-3`}>
            <p className="text-xs text-gray-700">{alert.message}</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <p className="text-gray-400 font-medium mb-0.5">Source</p>
                <p className="font-semibold text-gray-800">{alert.sourceLabel}</p>
              </div>
              {alert.framework && (
                <div>
                  <p className="text-gray-400 font-medium mb-0.5">Framework</p>
                  <p className="font-semibold text-gray-800">{alert.framework}</p>
                </div>
              )}
              {alert.dueDate && (
                <div>
                  <p className="text-gray-400 font-medium mb-0.5">Due Date</p>
                  <p className="font-semibold text-gray-800">{formatDate(alert.dueDate)}</p>
                </div>
              )}
              <div>
                <p className="text-gray-400 font-medium mb-0.5">Detected</p>
                <p className="font-semibold text-gray-800">{formatDate(alert.createdAt)}</p>
              </div>
              <div>
                <p className="text-gray-400 font-medium mb-0.5">Status</p>
                <p className="font-semibold text-gray-800 capitalize">{alert.status}</p>
              </div>
            </div>

            {/* Note section.
                Dismissed reads the same as resolved — read-only. Alerts closed by
                the detector carry the reason in resolution_notes ("Superseded by
                the Review Overdue alert…", "Duplicate of alert #N…"); without this
                they looked like silent manual dismissals (Asana 1218508267443267).
                Never editable here: that text is the audit record of an automatic
                close, not a user note. */}
            {alert.status === 'resolved' || alert.status === 'dismissed' ? (
              alert.notes ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs">
                  <p className="text-gray-400 font-medium mb-0.5">Note</p>
                  <p className="text-gray-700 whitespace-pre-wrap">{alert.notes}</p>
                </div>
              ) : null
            ) : (alert.status === 'active' || alert.status === 'acknowledged') && (
              editingNoteUid === alert.uid || !alert.notes ? (
                <div className="space-y-1">
                  <textarea
                    rows={2}
                    placeholder="Add a note..."
                    value={notesMap[alert.uid] || ''}
                    onChange={e => setNotesMap(prev => ({ ...prev, [alert.uid]: e.target.value }))}
                    onClick={e => e.stopPropagation()}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700 placeholder-gray-400"
                  />
                  {editingNoteUid === alert.uid && (
                    <button
                      onClick={e => { e.stopPropagation(); setEditingNoteUid(null); setNotesMap(prev => ({ ...prev, [alert.uid]: alert.notes || '' })); }}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs flex items-start justify-between gap-2">
                  <div>
                    <p className="text-gray-400 font-medium mb-0.5">Note</p>
                    <p className="text-gray-700 whitespace-pre-wrap">{alert.notes}</p>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); setEditingNoteUid(alert.uid); setNotesMap(prev => ({ ...prev, [alert.uid]: alert.notes || '' })); }}
                    className="shrink-0 text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Edit
                  </button>
                </div>
              )
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                onClick={e => { e.stopPropagation(); navigateToSource(alert); }}
                className="flex items-center gap-1 px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-700 transition-colors"
              >
                {alert.suggestedAction.label} <ArrowRight className="w-3 h-3" />
              </button>
              {alert.status === 'active' && (
                <button
                  onClick={e => { e.stopPropagation(); handleAction(alert.uid, 'acknowledge'); }}
                  disabled={isActing('acknowledge')}
                  className="px-3 py-1.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Acknowledge
                </button>
              )}
              {(alert.status === 'active' || alert.status === 'acknowledged') && notesMap[alert.uid]?.trim() && (
                <button
                  onClick={e => { e.stopPropagation(); handleSaveNote(alert.uid); }}
                  disabled={isActing('savenote')}
                  className="px-3 py-1.5 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 disabled:opacity-50 transition-colors"
                >
                  Save Note
                </button>
              )}
              {(alert.status === 'active' || alert.status === 'acknowledged') && (
                <button
                  onClick={e => { e.stopPropagation(); handleAction(alert.uid, 'resolve'); }}
                  disabled={isActing('resolve')}
                  className="px-3 py-1.5 border border-green-300 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100 disabled:opacity-50 transition-colors flex items-center gap-1"
                >
                  <CheckCircle className="w-3 h-3" /> Resolve
                </button>
              )}
              {(alert.status === 'active' || alert.status === 'acknowledged') && (
                <button
                  onClick={e => { e.stopPropagation(); handleAction(alert.uid, 'dismiss'); }}
                  disabled={isActing('dismiss')}
                  className="px-3 py-1.5 border border-gray-200 bg-white text-gray-400 rounded-lg text-xs font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Dismiss
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto space-y-6 px-4 py-6">

      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-600 rounded-xl">
              <Inbox className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Global Action Center</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {summary ? (
                  <span>
                    <span className={`font-bold ${summary.total > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {summary.total} open item{summary.total !== 1 ? 's' : ''}
                    </span>
                    {' '}across all Mission Centers
                  </span>
                ) : 'Loading…'}
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={() => fetchAlerts(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-xl text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Severity summary bar */}
      {renderSummaryBar()}

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Status tabs */}
        <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
          {STATUS_TABS.map(t => (
            <button
              key={t.value}
              onClick={() => setStatusTab(t.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                statusTab === t.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Severity + Source filters */}
        <div className="flex items-center gap-1.5 ml-auto">
          <Filter className="w-3.5 h-3.5 text-gray-400" />
          <select
            value={severityFilter}
            onChange={e => setSeverityFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          {/* Source filter */}
          <select
            value={sourceFilter}
            onChange={e => setSourceFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Sources</option>
            <option value="compliance_drift">Compliance Drift</option>
            <option value="vendor">Vendor Mgmt</option>
            <option value="asset">Asset Inventory</option>
            <option value="privileged_access">Privileged Access</option>
            <option value="security_scan">Security Scan</option>
            <option value="ai_ops">AI Operations</option>
            <option value="tenant_isolation">Tenant Isolation</option>
          </select>

          {(severityFilter || sourceFilter) && (
            <button
              onClick={() => { setSeverityFilter(''); setSourceFilter(''); }}
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-0.5"
            >
              <X className="w-3 h-3" />Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedUids.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-xl">
          <span className="text-sm font-medium text-blue-800">{selectedUids.size} selected</span>
          <button
            onClick={handleBulkDismiss}
            disabled={bulkDismissing}
            className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 text-white rounded-lg text-xs font-semibold hover:bg-gray-900 disabled:opacity-50 transition-colors"
          >
            <X className="w-3 h-3" />
            {bulkDismissing ? 'Dismissing…' : 'Dismiss Selected'}
          </button>
          <button
            onClick={() => setSelectedUids(new Set())}
            className="text-xs text-blue-600 hover:text-blue-800 ml-auto"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Alert list */}
      {loading ? (
        <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading alerts…</span>
        </div>
      ) : alerts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
          <CheckCircle className="w-10 h-10 text-green-400" />
          <p className="text-base font-semibold text-gray-600">No {statusTab} alerts</p>
          <p className="text-sm">All systems operating normally across Mission Centers.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.map(renderAlertRow)}
        </div>
      )}

      {/* Blueprint help text */}
      <div className="text-xs text-gray-400 pt-2 border-t border-gray-100">
        Aggregates alerts from: Compliance Drift · AI Operations · Vendor Management · Security Scan · Asset Inventory · Tenant Isolation · Privileged Access
      </div>
    </div>
  );
};

export default GlobalAlertInbox;
